import { createAsyncThunk } from '@reduxjs/toolkit'
import { COLLECTOR_URL } from '../../apiHelper/apiRoute/collector'
import { SETTINGS_URL } from '../../apiHelper/apiRoute/settings'
import { getPOSDisplay } from '../../components/common/shared/sharedFacilityLogic'
import { postFusion } from '../../apiHelper/fusionApi'
import { postCollector, putCollector } from '../../apiHelper/collectorApi'
import { getSettings } from '../../apiHelper/settingsApi'

/** Small helper method that handles any particular cases of errors fetching data from the API */
const handleError = (err) => {
  if (err.name === 'AxiosError' && err.response) {
    // Authorization has expired return to home screen for re-login
    console.error(err)
  }
}

/** Example object sent through filters in the request body (reqData.body)
 {
 "filter": {
 "encounter_statuses": ["in-progress", "completed"],
 "note_statuses": ["no-note", "draft"],
 "facilities": ["111111111111111", "2222222222222"],
 "providers": ["333333333333", "444444444444"],
 "procedure_codes": ["99654", "99655"],
 "service_date_from_ubound": "2023-10-30",
 "service_date_from_lbound": "2022-10-30"
 },
 "sort": {
 "service_date_from": -1
 }
 }
 */
function parseFilterData(oldFilter) {
  const filter = { ...oldFilter }
  for (let key in filter) {
    if (Array.isArray(filter[key]) && !filter[key].length) {
      //if the body data we are sending has empty arrays, then remove that key from reqData.body.filter
      delete filter[key]
    }
  }

  return filter
}

/** This thunk returns an encounters table based on the logged-in user and or a number of filters
 * @param reqData - An object literal that will have the property keys "limit" and "page." The values will be plugged into the final url string
 * @return object - Returns the response object from the API request
 */
export const getEncounters = createAsyncThunk(
  '/encounters/list',
  async ({ limit, page, scope, search, filter, sort } = {}, thunkAPI) => {
    try {
      // load current settings
      const encountersState = thunkAPI.getState()?.encounters

      const body = {
        limit: limit || encountersState.currentPageLimit,
        page: page || encountersState.currentPage,
        scope: scope || encountersState.currentScope,
        filter: parseFilterData(filter || { ...encountersState.currentFilters }),
        sort: sort || { ...encountersState.currentSort },
        ...((search || encountersState.currentSearch) && {
          search: { patient_name: search || encountersState.currentSearch },
        }),
      }

      let result = await postFusion('v1/encounter/list', body)

      if (result?.encounters) {
        return result
      } else {
        return 'Server Error'
      }
    } catch (err) {
      console.error('Encounters Page: Error getting encounters data: ', err)
      handleError(err)
      return false
    }
  },
)

export const refreshEncounters = createAsyncThunk('/encounters/refresh', async (_, thunkAPI) => {
  try {
    const encountersState = thunkAPI.getState()?.encounters

    let reqData = {
      body: {
        limit: encountersState.currentPageLimit,
        page: encountersState.currentPage,
        scope: encountersState.currentScope,
        sort: { ...encountersState.currentSort },
        filter: parseFilterData({ ...encountersState.currentFilters }),
        ...(encountersState.currentSearch && {
          search: { patient_name: encountersState.currentSearch },
        }),
      },
    }

    let result = await postFusion('v1/encounter/list', reqData.body)

    if (result?.encounters) {
      return result
    } else {
      return 'Server Error'
    }
  } catch (err) {
    console.error('Encounters Page: Error refreshing encounters data: ', err)
    handleError(err)
    return false
  }
})

/** This thunk returns a list of filters stored in the database
 * @return object - Returns the response object from the API request
 */
export const getFilterOptions = createAsyncThunk('/encounters/filter/options', async (scope) => {
  try {
    let result = await postFusion('v1/encounter/filter/options', { scope: scope || null })

    if (result?.filter_options) {
      return result.filter_options
    } else {
      return 'Server Error'
    }
  } catch (err) {
    console.error('Encounters Page: Error getting filters data: ', err)
    handleError(err)
    return false
  }
})

/** This thunk returns the facilities for the current provider
 * @return object - Returns the response object from the API request
 */
export const getFacilities = createAsyncThunk('currentprovider/facilities', async () => {
  try {
    const resFacilities = await getSettings(SETTINGS_URL.FACILITIES_BY_PROVIDER)

    if (resFacilities?.status !== 'success') {
      return 'Server Error'
    }

    return (
      resFacilities.results?.map((fac) => ({
        ...fac,
        facility_name: fac.name + getPOSDisplay(fac),
      })) || []
    )
  } catch (err) {
    console.error('Encounters Page: Error getting facilities data: ', err)
    handleError(err)
    return false
  }
})

/** This thunk will execute a bulk operation of voiding selected items in the encounters table.
 * /dailycensus/bulkvoid
 * This is a POST request that will take in a payload as a JSON object in the reqData.body {"visitIds": ["6543fc083f2f1bad67a18361, 6543fc083f2f1bad67a18361"], "status": "Checked In", "isVoid": true}
 * The visit_id is a list of visit ID statuses that are selected gathered together in an array
 */
export const bulkVoid = createAsyncThunk('dailycensus/bulkvoid', async (reqData) => {
  try {
    let result = await postCollector(COLLECTOR_URL.BULK_UPDATE, reqData.body)

    if (result?.status !== 'success') {
      return 'Server Error'
    }

    return result
  } catch (err) {
    console.error('Encounters Page: Error doing bulk void: ', err)
    handleError(err)
    return false
  }
})

/** This thunk will execute a bulk operation of recalling submissions of selected items in the encounters table.
 * This is a POST request that will take in a payload as a JSON object in the reqData.body e.g. {visit_id: ["6543fb743f2f1bb825a18270", "6544132d3f2f1b6d09a1a1f2"], messageText: ""}
 * The visit_id is a list of visit ID statuses in an array. The message seems to be irrelevant.
 */
export const bulkRecallSubmissions = createAsyncThunk(
  'dailycensus/v2/visits/bulkrecallbill',
  async (reqData) => {
    try {
      let result = await postCollector('dailycensus/v2/visits/bulkrecallbill', reqData.body)

      // collector issue we have not fixed yet TODO
      if (result?.status !== 'sucesss' && result?.status !== 'success') {
        return 'Server Error'
      }

      return result.data
    } catch (err) {
      console.error('Encounters Page: Error doing bulk recall: ', err)
      handleError(err)
      return false
    }
  },
)

export const bulkSkip = createAsyncThunk('encounters/bulkSkip', async (reqData) => {
  try {
    const result = await putCollector(COLLECTOR_URL.BULK_VISIT_STATUS, {
      status: Array(reqData.ids.length).fill('Not seen'),
      visit_id: reqData.ids,
    })

    // collector issue we have not fixed yet TODO
    if (result?.status !== 'sucesss' && result?.status !== 'success') {
      return 'Server Error'
    }

    return result.data
  } catch (err) {
    console.error('Encounters Page: Error doing bulk skip: ', err)
    handleError(err)
    return false
  }
})
